import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Button, Dialog, Combobox, Badge } from "@/components/ui";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Form, FormRequest, PlayersServices, FanServices, FormServices } from "@/services/bedrock";
import { useQuery } from "@tanstack/react-query";
import { usePaginatedCombobox } from "@/utils";
import { Copy, Check, Pencil, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface FormRequestsListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: Form | null;
  onSeeResponses?: (form: Form, requestId: number) => void;
  onEditRequest?: (req: FormRequest) => void;
}

function CopyLinkButton({ url }: { url?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex items-center gap-2.5 max-w-[380px]">
      <Button
        variant={copied ? "default" : "outline"}
        size="xs"
        onClick={handleCopy}
        className={cn(
          "h-7 text-[10px] px-2.5 rounded-lg flex items-center gap-1 transition-all duration-150 select-none shrink-0",
          copied ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 animate-pulse" : ""
        )}
      >
        {copied ? <Check className="size-3 shrink-0" /> : <Copy className="size-3 shrink-0" />}
        {copied ? "Copied" : "Copy"}
      </Button>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline font-mono truncate select-all flex-1"
          title={url}
        >
          {url}
        </a>
      ) : (
        <span className="text-slate-400 font-sans text-xs">-</span>
      )}
    </div>
  );
}

export function FormRequestsListDialog({
  open,
  onOpenChange,
  form,
  onSeeResponses,
  onEditRequest,
}: FormRequestsListDialogProps) {
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

  // Step 1: get players list for filter
  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      })),
    };
  };

  const players = usePaginatedCombobox(getPlayers, false);

  // Step 2: get fans list for filter (filtered by player if selected)
  const getFans = useCallback(
    async (search: string | undefined, page: number) => {
      const result = await FanServices.getFans({
        search,
        page,
        perPage: 25,
        playerId: players.value?.id,
      } as any);
      return {
        ...result,
        content: result.content.map(fan => ({
          id: fan.id,
          label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
          original: fan,
        })),
      };
    },
    [players.value]
  );

  const fans = usePaginatedCombobox(getFans, false);

  // Clear fan selection when player selection changes
  useEffect(() => {
    if (fans.value !== null && fans.value !== undefined) {
      fans.onValueChange(null as any, undefined);
    }
  }, [players.value]);

  // Reset filter selections when opening dialog
  useEffect(() => {
    if (open) {
      setPagination(prev => {
        if (prev.page === 1 && prev.perPage === 10) return prev;
        return { page: 1, perPage: 10 };
      });
      if (players.value !== null && players.value !== undefined) {
        players.onValueChange(null as any, undefined);
      }
      if (fans.value !== null && fans.value !== undefined) {
        fans.onValueChange(null as any, undefined);
      }
    }
  }, [open]);

  // Fetch paginated requests list with selected filters
  const { data, isLoading } = useQuery({
    queryKey: ["formRequestsList", form?.id, pagination, players.value?.id, fans.value?.id],
    queryFn: () =>
      FormServices.getFormRequests({
        formId: form?.id,
        playerId: players.value?.id,
        fanId: fans.value?.id,
        page: pagination.page,
        perPage: pagination.perPage,
      } as any),
    enabled: !!form?.id && open,
  });

  // Table Columns Definition
  const columns = useMemo<ColumnDef<FormRequest>[]>(
    () => [
      {
        id: "assignedTo",
        header: "Assigned To",
        cell: ({ row }) => {
          const req = row.original;
          const playerLabel = req.player?.fullName ? `Player: ${req.player.fullName}` : null;
          const fanLabel = req.fan?.displayName ? `Fan: ${req.fan.displayName}` : null;

          if (!playerLabel && !fanLabel) {
            return <span className="text-slate-400 italic text-xs">General Link</span>;
          }

          return (
            <div className="flex flex-col gap-0.5 text-xs font-sans">
              {playerLabel && <span className="font-semibold text-slate-700">{playerLabel}</span>}
              {fanLabel && <span className="text-slate-500">{fanLabel}</span>}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const req = row.original;
          const status = req.status || "ready";
          const isReady = status === "ready";
          const isFailed = status === "failed";
          const hasAi = !!req.useAiPersonalization;

          return (
            <div className="flex items-center gap-1.5 select-none">
              <Badge
                variant={isReady ? "default" : isFailed ? "destructive" : "outline"}
                className={cn(
                  "text-[10px] px-1.5 py-0 font-bold uppercase",
                  isReady ? "bg-emerald-500 border-emerald-500 text-white" : ""
                )}
              >
                {status}
              </Badge>
              {hasAi && (
                <Sparkles
                  className="size-3.5 text-purple-600 animate-pulse shrink-0"
                  title="AI Personalization Enabled"
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "totalResponseCount",
        header: "Responses",
        cell: ({ row }) => {
          const req = row.original;
          const count = req.totalResponseCount ?? 0;
          return (
            <button
              type="button"
              className="font-semibold font-mono text-xs text-primary hover:underline cursor-pointer select-none"
              onClick={e => {
                e.stopPropagation();
                if (onSeeResponses && form) {
                  onSeeResponses(form, req.id);
                }
              }}
            >
              {count}
            </button>
          );
        },
      },
      {
        accessorKey: "url",
        header: "Form Link",
        cell: ({ row }) => <CopyLinkButton url={row.original.url} />,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const req = row.original;
          return (
            <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400 hover:text-slate-800"
                onClick={() => {
                  if (onEditRequest) {
                    onEditRequest(req);
                  }
                }}
                title="Edit Settings"
              >
                <Pencil className="size-3.5" />
              </Button>
            </div>
          );
        },
      },
    ],
    [form, onSeeResponses, onEditRequest]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <Dialog.Header>
        <Dialog.Title>Form Link History</Dialog.Title>
        <Dialog.Description>
          Browse and filter all generated request links for <strong>{form?.displayName || "this form"}</strong>.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col gap-5 py-4 max-h-[70vh] overflow-y-auto">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-center select-none">
          <div className="flex items-center gap-2 w-full sm:w-80">
            <Combobox {...players} placeholder="Filter by Player..." className="flex-1 bg-white" showClear />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-80">
            <Combobox
              {...fans}
              placeholder={players.value ? "Filter by Player's Fans..." : "Filter by Fan..."}
              className="flex-1 bg-white"
              showClear
            />
          </div>
        </div>

        {/* Requests Data Table */}
        <div className="flex-1 min-h-[300px]">
          <DataTable
            columns={columns}
            data={data?.content ?? []}
            loading={isLoading}
            pagination={pagination}
            onPaginationChange={setPagination}
            pageCount={data?.pagination?.totalPages}
            rowCount={data?.pagination?.totalCount}
          />
        </div>
      </Dialog.Content>

      <Dialog.Footer>
        <Button onClick={() => onOpenChange(false)} type="button">
          Close
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
